/**
 * Calendar Migration Utility
 * Converts old calendar format to new flexible events format
 * Also extracts calendar events from registration_steps and exams
 */

import { Calendar, CalendarEvent, Concours } from '../types/concours';

// Old calendar format (for backward compatibility)
interface OldCalendar {
  preregistration?: CalendarEvent;
  payment?: CalendarEvent;
  registration?: CalendarEvent;
  document_submission?: CalendarEvent;
  medical_exam?: CalendarEvent;
  candidates_list?: CalendarEvent;
  written_exam?: CalendarEvent;
  oral_exam?: CalendarEvent;
  results?: CalendarEvent;
}

/**
 * Extracts calendar events from registration_steps
 */
function extractEventsFromSteps(registrationSteps: any[]): CalendarEvent[] {
  if (!registrationSteps || !Array.isArray(registrationSteps)) return [];

  const events: CalendarEvent[] = [];

  for (const step of registrationSteps) {
    if (step.period && (step.period.start || step.period.end)) {
      events.push({
        label: step.name.toUpperCase(),
        type: 'period',
        start: step.period.start,
        end: step.period.end,
      });
    }
  }

  return events;
}

/**
 * Extracts calendar events from exams
 */
function extractEventsFromExams(exams: any[]): CalendarEvent[] {
  if (!exams || !Array.isArray(exams)) return [];

  const events: CalendarEvent[] = [];

  for (const exam of exams) {
    if (exam.date) {
      events.push({
        label: exam.name.toUpperCase(),
        type: 'date',
        date: exam.date,
      });
    }
  }

  return events;
}

/**
 * Extracts calendar from medical_exam data
 */
function extractMedicalExamEvent(medicalExam: any): CalendarEvent | null {
  if (!medicalExam || !medicalExam.required) return null;

  if (medicalExam.registration_period?.start && medicalExam.registration_period?.end) {
    return {
      label: 'VISITE MÉDICALE',
      type: 'period',
      start: medicalExam.registration_period.start,
      end: medicalExam.registration_period.end,
    };
  }

  return null;
}

/**
 * Migrates old calendar format to new events array format
 * Also extracts events from registration_steps, exams, and medical_exam
 */
export function migrateCalendar(calendar: any, concoursData?: Partial<Concours>): Calendar {
  let events: CalendarEvent[] = [];

  // If already in new format with events, use it
  if (calendar?.events && Array.isArray(calendar.events) && calendar.events.length > 0) {
    return calendar as Calendar;
  }

  // Try to extract from old calendar format
  if (calendar && typeof calendar === 'object' && !Array.isArray(calendar.events)) {
    const oldCalendar = calendar as OldCalendar;

    const eventMappings = [
      { key: 'preregistration', label: 'PRÉINSCRIPTION' },
      { key: 'payment', label: 'PAIEMENT DU DROIT D\'INSCRIPTION' },
      { key: 'registration', label: 'INSCRIPTION' },
      { key: 'document_submission', label: 'DÉPÔT DES DOSSIERS DANS LES DRENA' },
      { key: 'medical_exam', label: 'VISITE MÉDICALE' },
      { key: 'candidates_list', label: 'MISE EN LIGNE DE LA LISTE DES CANDIDATS AUTORISÉS' },
      { key: 'written_exam', label: 'ÉPREUVES ÉCRITES' },
      { key: 'oral_exam', label: 'ÉPREUVES ORALES' },
      { key: 'results', label: 'RÉSULTATS' },
    ];

    for (const mapping of eventMappings) {
      const eventData = oldCalendar[mapping.key as keyof OldCalendar];
      if (eventData) {
        const hasPeriod = eventData.start && eventData.end;
        events.push({
          label: eventData.label || mapping.label,
          type: hasPeriod ? 'period' : 'date',
          start: eventData.start,
          end: eventData.end,
          date: eventData.date,
        });
      }
    }
  }

  // If no events from calendar, try to extract from other sources
  if (events.length === 0 && concoursData) {
    // Extract from registration_steps
    if (concoursData.registration_steps) {
      events = events.concat(extractEventsFromSteps(concoursData.registration_steps));
    }

    // Extract from medical_exam
    if (concoursData.medical_exam) {
      const medicalEvent = extractMedicalExamEvent(concoursData.medical_exam);
      if (medicalEvent) events.push(medicalEvent);
    }

    // Extract from exams
    if (concoursData.exams) {
      events = events.concat(extractEventsFromExams(concoursData.exams));
    }
  }

  // Remove duplicates based on label
  const uniqueEvents = events.filter((event, index, self) =>
    index === self.findIndex((e) => e.label === event.label)
  );

  return { events: uniqueEvents };
}

/**
 * Checks if a calendar is in old format
 */
export function isOldCalendarFormat(calendar: any): boolean {
  if (!calendar || typeof calendar !== 'object') return false;
  if (calendar.events && Array.isArray(calendar.events)) return false;

  // Check for old format keys
  const oldKeys = ['preregistration', 'payment', 'registration', 'document_submission',
    'medical_exam', 'candidates_list', 'written_exam', 'oral_exam', 'results'];
  return oldKeys.some(key => key in calendar);
}
